import { initAllStores } from "./cache";
import Service from "../models/Service";
import { NavigateFunction } from "react-router-dom";
import { terminal } from "./user-helper";

export const loadServiceItems = async (
  serviceType: ServiceType
): Promise<[(Service & { itemType: ServiceType })[], string[]]> => {
  const cache = await initAllStores();

  const terminalId = terminal();

  const filter = serviceType === "foods" || serviceType === "drinks"
    ? { terminal: terminalId } : {};
  const stored: Service[] = await cache.getAllBy(
    serviceType,
    filter,
    "name"
  );

  const serviceItems: (Service & { itemType: ServiceType })[] = stored.map((d: any) => ({
    id: d.id,
    name: d.name,
    cashPrice: d.cashPrice ?? 0,
    openBar: d.openBar,
    gameHouse: d.gameHouse,
    vipLounge: d.vipLounge,
    privateLounge: d.privateLounge,
    clubHouse: d.clubHouse,
    itemType: serviceType,
    description: d.description,
    category:
      d.drinkCategory?.name ||
      d.foodCategory?.name ||
      d.gameCategory?.name ||
      d.laundryCategory?.name ||
      "Uncategorized",
  }));

  // ✅ Extract unique categories correctly
  const uniqueCategories = Array.from(
    new Set(serviceItems.map((d) => d.category))
  );

  return [serviceItems, uniqueCategories];
};


export const toBool = (strBool: "true" | "false"): boolean => {
  const lookup = {
    "true": true,
    "false": false,
  };

  return lookup[strBool];
};

export const isOnline = (): boolean => {
  const isOnline: "true" | "false" | null = localStorage.getItem("isOnline") as "true" | "false" | null;
  if (!isOnline) return false;
  return toBool(isOnline);
}


export const getService = async (
  serviceType: ServiceType | undefined, itemId: string | undefined
): Promise<Service | null> => {
  const cache = await initAllStores();
  if (!serviceType || !itemId) return null;
  const service = await cache.getBy(serviceType, { id: itemId });
  return service;
};


/**
 * Enriches order items with their service name and adds optional extra properties.
 * Defaults name to "Deleted Item" if service is missing.
 *
 * @param {OrderItem[]} items - List of order items to enrich.
 * @param {Record<string, any>} [extra] - Optional extra properties to merge into each item.
 * @returns {Promise<(Service & { name: string } & Record<string, any>)[]>}
 */
// export async function enrichOrderItems(
//   items: OrderItem[] = [],
//   extra?: Record<string, any>
// ): Promise<(Service & Record<string, any>)[]> {
//   return Promise.all(
//     items.map(async (item) => {
//       const serviceItem = await getService(item.itemType, item.itemId);

//       return {
//         ...item,
//         name: serviceItem?.name ?? "Deleted Item",
//         ...extra, // attach extra properties like { isOrder: true }
//       };
//     })
//   );
// }


export function shuffle(array: any[]) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

/**
 * Checks if a given value is an empty plain object.
 * @param obj - The value to check.
 * @return True if value is an empty plain object, otherwise false.
 */
export const isEmptyObject = (obj: unknown): boolean => {
  if (!obj) {
    return true;
  }
  return obj.constructor === Object && Object.keys(obj).length === 0;
}

export function getQueryParam(param: string): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get(param);
}


export const getCurrentHostInfo = () => {
  const protocol = window.location.protocol; // http: or https:
  const hostname = window.location.hostname; // e.g., demo-hotel.wiventory.com
  const hostParts = hostname.split(".");     // ["demo-hotel", "wiventory", "com"]
  const subdomain = hostParts[0];

  return {
    protocol,
    hostname,
    hostParts,
    subdomain
  };
};


export const getUrl = (useSubdomain?: boolean) => {
const { protocol, hostname, subdomain } = getCurrentHostInfo();
const portNumber = protocol === "http:" ? ":3001" : "";
return `${protocol}//${useSubdomain ? `${subdomain}.` : ""}${hostname.split(".")[1]}${protocol === "http:" ? "" : ".com"}${portNumber}`;
}

/**
 * Validates that a subdomain exists in the current hostname.
 * If missing, navigates to the 404 page.
 */
export const validateSubdomain = (navigate: NavigateFunction): string => {
  const parts: string[] = window.location.hostname.split('.');

  // handle cases like demo-hotel.wiventory.com or demo-hotel.localhost
  const subdomain: string = parts[0];

  if (!subdomain) {
    navigate("/404");
    return "";
  }
  return subdomain;
};